import Fuse from "fuse.js";
import { useEffect, useRef, useState } from "react";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import {
  getStoredContentFilters,
  storeContentFilters,
} from "../../domains/app/utils/contentFilters";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import type { OnboardingTag } from "../../domains/auth/hooks/useSignupOnboardingState";
import type { PrivateRoom } from "../../domains/room/privateRoom";
import { apiClient } from "../../services/api";
import { gaEventSetFilter } from "../../tools/analytics/domains/filter.analytics";
import { Button } from "../ui/Button";
import { SectionTitle } from "../ui/SectionTitle";
import AppCheckbox from "../AppCheckbox";
import CustomIcon from "../CustomIcon";
import { Input } from "../ui/Input";
import { IconSearch, IconX } from "@tabler/icons-react";
import {
  FilterPanelActionBox,
  FilterPanelContent,
  FilterPanelFooter,
  FilterPanelHeader,
  LeftDrawer,
  SelectableItem,
} from "./layout";
import { useTranslation } from "react-i18next";

export default function SubSpecialtyFilterPanel({
  specialty,
  room,
  isOpen,
  onClose,
  onSave,
  // NOTE: Used to force a set of subspecialties instead of the ones from the defined specialty
  subSpecialties,
}: {
  specialty: OnboardingTag;
  room: PrivateRoom | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  subSpecialties?: OnboardingTag[];
}) {
  const { activeLang } = useActiveLang();
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [fetchedSubSpecialties, setFetchedSubSpecialties] = useState<
    OnboardingTag[]
  >(subSpecialties ?? []);
  const [selectedSubSpecialtyIds, setSubSelectedSpecialtyIds] = useState<
    string[]
  >([]);
  const [memSubSelectedSpecialtyIds, setMemSubSelectedSpecialtyIds] = useState<
    string[]
  >([]);
  const isAllSelected =
    selectedSubSpecialtyIds.length === fetchedSubSpecialties.length;
  const [visibleItems, setVisibleItems] = useState(fetchedSubSpecialties);
  const sortedItems = [...visibleItems].sort((a, b) => {
    return getItemTranslation(a).localeCompare(getItemTranslation(b));
  });
  const [initialSelectedIds, setInitialSelectedIds] = useState<string[]>([]);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Sync fetched subspecialties when prop changes (e.g. switching rooms)
  useEffect(() => {
    if (subSpecialties) {
      setFetchedSubSpecialties(subSpecialties);
      setVisibleItems(subSpecialties);
    }
  }, [subSpecialties]);

  useEffect(() => {
    initialize();
  }, [specialty.uid]);

  useEffect(() => {
    if (isOpen) {
      const storedFilters = getStoredContentFilters();
      setSubSelectedSpecialtyIds(storedFilters.subSpecialtyIds);
      setMemSubSelectedSpecialtyIds(selectedSubSpecialtyIds);
      setInitialSelectedIds(storedFilters.subSpecialtyIds);
    } else {
      setTimeout(() => {
        setSearchValue("");
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchValue) {
      const fuse = new Fuse(fetchedSubSpecialties, {
        includeScore: true,
        threshold: 0.25,
        keys: [`translations.${activeLang}`],
        ignoreDiacritics: true,
      });
      const searchResults = fuse.search(searchValue).map((res) => res.item);
      setVisibleItems(searchResults);
    } else {
      setVisibleItems(fetchedSubSpecialties);
    }
  }, [searchValue]);

  async function initialize() {
    try {
      if (!subSpecialties) {
        const response = await apiClient.get(
          "/webapp/public/tags/by-parent/:uid",
          { param: { uid: specialty.uid } }
        );
        setFetchedSubSpecialties(response.body);
        setVisibleItems(response.body);
      }
    } catch {
      // api error already captured by onRequestError.
    }
  }

  function handleToggleSelect(tag: OnboardingTag): void {
    if (selectedSubSpecialtyIds.includes(tag.id)) {
      setSubSelectedSpecialtyIds(
        selectedSubSpecialtyIds.filter((id) => id !== tag.id)
      );
    } else {
      setSubSelectedSpecialtyIds([...selectedSubSpecialtyIds, tag.id]);
    }
  }

  function handleSelectAll() {
    setSubSelectedSpecialtyIds(fetchedSubSpecialties.map((tag) => tag.id));
  }

  function handleUnselectAll() {
    setSubSelectedSpecialtyIds([]);
  }

  function handleCloseWithoutSaving() {
    onClose();
    setTimeout(() => {
      setSubSelectedSpecialtyIds(memSubSelectedSpecialtyIds);
    }, 100);
  }

  function handleSubmit() {
    const subSpecialties = selectedSubSpecialtyIds
      .map((id) => fetchedSubSpecialties.find((tag) => tag.id === id))
      .filter((tag) => tag !== undefined);
    storeContentFilters({
      specialtyIds: selectedSubSpecialtyIds.length > 0 ? [specialty.id] : [],
      specialtyUids: selectedSubSpecialtyIds.length > 0 ? [specialty.uid] : [],
      subSpecialtyIds: selectedSubSpecialtyIds,
      subSpecialtyUids: subSpecialties.map((tag) => tag.uid),
    });

    if (subSpecialties.length > 0) {
      gaEventSetFilter({
        specialties: [specialty],
        tags: subSpecialties,
        roomName: room?.translations.en.name,
      });
    }
    onSave();
    onClose();
  }

  return (
    <LeftDrawer isOpen={isOpen}>
      <FilterPanelHeader>
        <CustomIcon
          className="close-button"
          iconName="close_alt"
          color="var(--color-secondary-bluegrey-7)"
          onClick={handleCloseWithoutSaving}
        />
      </FilterPanelHeader>

      <FilterPanelContent ref={scrollerRef}>
        <span className="info">
          {t("Filter by subspecialties within")}{" "}
          <b>{getItemTranslation(specialty, activeLang)}</b>
        </span>

        <Input
          value={searchValue}
          placeholder={t("Search")}
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
          trailing={
            searchValue.length === 0 ? (
              <IconSearch className="size-5 text-ds-slate-400" />
            ) : (
              <button
                type="button"
                aria-label={t("Clear search")}
                className="flex size-8 items-center justify-center rounded-full text-ds-slate-400 hover:text-ds-black"
                onClick={() => {
                  setSearchValue("");
                }}
              >
                <IconX className="size-4" />
              </button>
            )
          }
        />

        {searchValue.length === 0 && (
          <div>
            <SectionTitle title={t("Default")} className="my-4" />

            <FilterPanelActionBox>
              <AppCheckbox
                checked={isAllSelected}
                onChange={(checked) => {
                  if (checked) handleSelectAll();
                  else handleUnselectAll();
                }}
              >
                <span className="text-sm font-bold text-primary-black">
                  {t("Select all")}
                </span>
              </AppCheckbox>

              <div
                className={`clear-all ${selectedSubSpecialtyIds.length > 0 ? "active" : ""}`}
                onClick={handleUnselectAll}
              >
                <CustomIcon iconName="times-radio" />
                {t("Clear all")}
              </div>
            </FilterPanelActionBox>
          </div>
        )}

        <hr style={{ margin: "16px 0" }} />

        <SectionTitle title={t("Subspecialties")} className="my-4" />

        {[
          ...sortedItems.filter((tag) => initialSelectedIds.includes(tag.id)),
          ...sortedItems.filter((tag) => !initialSelectedIds.includes(tag.id)),
        ].map((tag) => {
          const isSelected = selectedSubSpecialtyIds.includes(tag.id);
          return (
            <SelectableItem key={tag.id}>
              <span>{getItemTranslation(tag, activeLang)}</span>
              <Button
                variant={isSelected ? "secondary" : "outline"}
                size="md"
                onClick={() => {
                  handleToggleSelect(tag);
                }}
              >
                {isSelected ? t("Selected") : t("Select")}
              </Button>
            </SelectableItem>
          );
        })}
      </FilterPanelContent>

      <FilterPanelFooter>
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={handleSubmit}
        >
          {t("Apply")}
        </Button>
      </FilterPanelFooter>
    </LeftDrawer>
  );
}
